import { ParsedUrlQuery } from 'querystring';
import querystring from 'query-string';

import type { SsrAppApiDirectOptions } from '@/data/appApi';
import { Prices } from '@/store/prices/types';
import apiRequest from '@/utils/apiRequest';
import { RequestUserData } from '@/utils/apiRequest/types';
import { calmLogger } from '@/utils/calmLogger';
import getParamAsString from '@/utils/getParamsAsString';

import { mergeCatalogPriceIntoPrices } from '../../utils/prices/mergeCatalogPriceIntoPrices';
import { getFamilyPrices } from './getFamilyPrices';

const getCouponFromQuery = (query: ParsedUrlQuery) => {
	return query.coupon && typeof query.coupon !== 'object' ? getParamAsString(query.coupon) : null;
};

export const createPricesEndpoint = (query: ParsedUrlQuery, coupon?: string | string[] | null): string => {
	const pricesQueryParams = querystring.stringify({
		...query,
		...(typeof coupon === 'string' ? { coupon } : {}),
	});

	return `subscription/prices?${pricesQueryParams}`;
};

export async function getPrices(
	query: ParsedUrlQuery,
	userData: RequestUserData,
	opts?: SsrAppApiDirectOptions,
): Promise<Prices | null> {
	let sanitizedQuery = { ...query };
	if (query.guestPass && query.coupon) {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { coupon, ...rest } = query;
		sanitizedQuery = { ...rest };
	}

	const coupon = getCouponFromQuery(sanitizedQuery);
	const pricesEndpoint = createPricesEndpoint(sanitizedQuery, coupon);

	// SSR app-api-direct: route the subscription-prices fetch straight to
	// internal app-api when the kill-switch is ON. `requiresAuth: true` matches the
	// authproxy (Bearer injected for this endpoint). See getFeatureFlags.ts for the
	// lazy-import rationale.
	const fetchSubPrices = async (): Promise<Prices | null> => {
		if (typeof window === 'undefined' && opts?.ctx) {
			const { appApiServerRequest } = await import('@/data/appApi');
			const { data } = await appApiServerRequest<Prices>({
				endpoint: pricesEndpoint,
				ctx: opts.ctx,
				xLanguage: opts.xLanguage,
				requiresAuth: true,
			});
			// `<Prices>` only asserts the expected shape, not the runtime one — a 2xx
			// with an empty body deserializes to a falsy value here.
			return data || null;
		}
		const result = await apiRequest<Prices>({
			endpoint: pricesEndpoint,
			method: 'GET',
			userData,
		});
		return result.data || null;
	};

	try {
		const [subPrices, familyPrice] = await Promise.all([
			fetchSubPrices(),
			getFamilyPrices(userData, opts, coupon),
		]);

		const shapedData = familyPrice
			? mergeCatalogPriceIntoPrices(familyPrice, subPrices, 'family')
			: subPrices;
		return shapedData ?? null;
	} catch (err) {
		calmLogger.error(`Error retrieving prices via GET /${pricesEndpoint}`, {}, err);
		return null;
	}
}
